import type {components} from "../../../api/generated/schema";
import {ApiError} from "../../shared/api/client";
import {sessionStore, SessionOwnerChangedError, type SessionOwner, type SessionStore} from "../../shared/session/session-store";
import type {AccountLifecycle} from "./ports";
import type {AccountDeletionConfirm, AccountDeletionPreview, AccountDeletionResult, CabinetLoadOptions, DeletionBlocker, DeletionNextAction} from "./model";

const base = "/api/v1/auth/browser/account-deletion";
const blockerCodes: DeletionBlocker[] = ["wallet_balance_non_zero", "wallet_hold_active", "financial_settlement_pending", "payment_pending", "consultation_order_active", "booking_active", "payout_pending", "owner_unavailable"];
const nextActions: DeletionNextAction[] = ["wallet", "orders", "support", "retry"];
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
function invalid(): never { throw new ApiError(500); }
function record(value: unknown, required: string[], optional: string[] = []): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return invalid();
  const row = value as Record<string, unknown>;
  if (required.some((key) => !(key in row)) || Object.keys(row).some((key) => !required.includes(key) && !optional.includes(key))) return invalid();
  return row;
}
function operation(value: unknown): string { if (typeof value !== "string" || !uuid.test(value)) return invalid(); return value; }
function timestamp(value: unknown): string {
  if (typeof value !== "string" || !/^20\d{2}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/.test(value) || !Number.isFinite(Date.parse(value))) return invalid();
  const date = new Date(value);
  if (date.toISOString().slice(0,19) !== value.slice(0,19)) return invalid();
  return value;
}
function blockers(value: unknown): Pick<AccountDeletionResult,"blockers"|"nextActions"> {
  if (!Array.isArray(value) || value.length > 8) return invalid();
  const result: DeletionBlocker[] = [];
  const actions: Partial<Record<DeletionBlocker, DeletionNextAction>> = {};
  let previous = -1;
  for (const item of value) {
    const row = record(item,["code","next_action"]);
    const code = row.code as DeletionBlocker, action = row.next_action as DeletionNextAction;
    const index = blockerCodes.indexOf(code);
    if (index <= previous || !nextActions.includes(action)) return invalid();
    previous = index;
    result.push(code); actions[code] = action;
  }
  return {blockers:result,nextActions:actions};
}
export function decodeDeletionPreview(value: unknown): AccountDeletionPreview {
  const row = record(value,["schema_version","operation_id","handle_expires_at","blockers"]);
  if (row.schema_version !== 1) return invalid();
  return {operationId:operation(row.operation_id),handleExpiresAt:timestamp(row.handle_expires_at),confirmationHint:"",...blockers(row.blockers)};
}
export function decodeDeletionResult(value: unknown): AccountDeletionResult {
  const row = record(value,["schema_version","operation_id","state","blockers"],["processing_phase","retry_reason","next_action","anonymized_at"]);
  if (row.schema_version !== 1) return invalid();
  const common = {operationId:operation(row.operation_id),...blockers(row.blockers)};
  switch (row.state) {
    case "processing":
      if (!["preparing","sealing","releasing","committing"].includes(row.processing_phase as string) || row.retry_reason !== undefined || row.next_action !== undefined || row.anonymized_at !== undefined || common.blockers.length) return invalid();
      return {...common,state:"processing",processingPhase:row.processing_phase as AccountDeletionResult["processingPhase"]};
    case "blocked":
      if (!common.blockers.length || row.processing_phase !== undefined || row.retry_reason !== undefined || row.next_action !== undefined || row.anonymized_at !== undefined) return invalid();
      return {...common,state:"blocked"};
    case "retryable":
      if (!["owner_unavailable","prepare_expired","operation_conflict"].includes(row.retry_reason as string) || row.next_action !== "retry" || row.processing_phase !== undefined || row.anonymized_at !== undefined || common.blockers.length) return invalid();
      return {...common,state:"retryable",retryReason:row.retry_reason as AccountDeletionResult["retryReason"]};
    case "anonymized":
      if (row.processing_phase !== undefined || row.retry_reason !== undefined || row.next_action !== undefined || common.blockers.length) return invalid();
      return {...common,state:"anonymized",anonymizedAt:timestamp(row.anonymized_at)};
    default: return invalid();
  }
}

async function parseResponse<T>(response: Response, decode: (value: unknown) => T): Promise<T> {
  if (response.headers.get("Cache-Control") !== "private, no-store" || response.headers.get("Pragma") !== "no-cache" || response.headers.has("ETag")) return invalid();
  const body: unknown = await response.json();
  if (!response.ok) {
    const row = record(body,["error"]);
    const detail = record(row.error,["code","message","request_id"],["fields"]);
    const codes = ["confirmation_invalid","unauthenticated","not_found","conflict","rate_limited","unavailable","internal"];
    if (!codes.includes(detail.code as string) || typeof detail.request_id !== "string" || detail.request_id.length > 128) return invalid();
    throw new ApiError(response.status,{error:{code:detail.code as string,message:"",request_id:detail.request_id}});
  }
  return decode(body);
}

export class ApiAccountLifecycle implements AccountLifecycle {
  constructor(private readonly owner: SessionOwner = sessionStore.captureOwner(), private readonly session: SessionStore = sessionStore) {}
  private guard(): void { if (!this.session.isCurrentOwner(this.owner)) throw new SessionOwnerChangedError(); }
  async previewDeletion(options?: CabinetLoadOptions): Promise<AccountDeletionPreview> {
    this.guard();
    const response = await this.session.authorizedOwnerFetch(this.owner,base+"/preview",{method:"POST",headers:{"Idempotency-Key":crypto.randomUUID()},signal:options?.signal});
    const result = await parseResponse(response,decodeDeletionPreview);
    this.guard();
    return result;
  }
  async confirmDeletion(input: AccountDeletionConfirm): Promise<AccountDeletionResult> {
    this.guard();
    const body: components["schemas"]["AccountDeletionConfirmInput"] = {operation_id:input.operationId,contact_kind:input.contactKind,contact:input.contact};
    const response = await this.session.authorizedOwnerCommandOnce(this.owner,base+"/confirm",{method:"POST",headers:{"Content-Type":"application/json","Idempotency-Key":input.key},body:JSON.stringify(body)});
    const result = await parseResponse(response,decodeDeletionResult);
    this.guard();
    if (result.operationId !== input.operationId) return invalid();
    return result;
  }
}

export async function getAccountDeletionStatus(signal?: AbortSignal, fetchImpl: typeof fetch = window.fetch.bind(window)): Promise<AccountDeletionResult> {
  const response = await fetchImpl(base+"/status",{method:"GET",credentials:"same-origin",cache:"no-store",signal});
  return parseResponse(response,decodeDeletionResult);
}
